import Phaser from 'phaser';
import halloween from '../../../server/api/src/game-config/halloween.config.json';
import { halloweenBossApi, type HalloweenBossView } from '@/net/api';
import { startTeleport } from '@/game/fx/teleport';
import { WorldInteractionController } from './WorldInteractionController';
import { canUseWorldInteraction } from './WorldInteractionMarker';
import { useGameStore } from '@/ui/store/gameStore';
import { isEventActive } from '@/config/events.config';

/** Local, bounded effects: one reusable gradient, two rotating arcs and six sparks. */
function createPortalVisuals(scene: Phaser.Scene, x: number, y: number) {
  const loops: Phaser.Tweens.Tween[] = [];
  const loop = (config: Phaser.Types.Tweens.TweenBuilderConfig): void => {
    loops.push(scene.tweens.add({ repeat: -1, ...config }));
  };
  const halo = scene.add.ellipse(x, y + 49, 205, 65, 0xffc878, 0.42).setDepth(-48);
  loop({ targets: halo, alpha: { from: 0.5, to: 0.95 }, duration: 1800, yoyo: true, ease: 'Sine.easeInOut' });

  const textureKey = 'fx-halloween-boss-portal';
  if (!scene.textures.exists(textureKey)) {
    const texture = scene.textures.createCanvas(textureKey, 128, 128);
    if (texture) {
      const context = texture.context;
      const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
      gradient.addColorStop(0, 'rgba(255,247,213,0.98)');
      gradient.addColorStop(0.3, 'rgba(255,211,159,0.94)');
      gradient.addColorStop(0.65, 'rgba(202,147,241,0.85)');
      gradient.addColorStop(0.84, 'rgba(157,105,217,0.68)');
      gradient.addColorStop(1, 'rgba(202,175,255,0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, 128, 128);
      texture.refresh();
    }
  }
  const disc = scene.add.image(0, 0, textureKey).setDisplaySize(140, 130);
  const rim = scene.add.ellipse(0, 0, 100, 110).setStrokeStyle(2, 0xffe6b6, 0.8);
  const swirl = scene.add.graphics().setName('halloween-boss-portal-swirl');
  swirl.lineStyle(3, 0xfff0cf, 0.85).beginPath().arc(0, 0, 36, 0.2, 2.2).strokePath();
  swirl.lineStyle(2, 0xffffff, 0.65).beginPath().arc(0, 0, 24, 3.3, 5.6).strokePath();
  swirl.fillStyle(0xfff4d0).fillCircle(35, 7, 3).fillCircle(-24, -4, 2);
  const energy = scene.add.container(x, y - 9, [disc, rim, swirl])
    .setDepth(3).setName('halloween-boss-portal-energy');
  loop({ targets: swirl, angle: 360, duration: 6500, ease: 'Linear' });
  loop({ targets: energy, scaleX: 1.04, scaleY: 1.04, duration: 1600, yoyo: true, ease: 'Sine.easeInOut' });

  const gate = scene.add.image(x, y - 25, 'deco_halloween_pumpkin_gate')
    .setDisplaySize(240, 205).setDepth(4).setName('halloween-boss-portal');
  loop({ targets: gate, y: y - 29, duration: 2000, yoyo: true, ease: 'Sine.easeInOut' });
  const sparks = scene.add.container(x, y - 14).setDepth(5);
  for (let i = 0; i < 6; i++) {
    const angle = i * Math.PI / 3;
    const star = scene.add.star(Math.cos(angle) * 79, Math.sin(angle) * 58, 4, 1.5, 4, 0xfff3be);
    sparks.add(star);
    loop({ targets: star, y: star.y - 14, alpha: { from: 0.2, to: 0.9 },
      scale: { from: 0.65, to: 1 }, duration: 1300 + i * 140, delay: i * 180,
      yoyo: true, ease: 'Sine.easeInOut' });
  }

  // Small hand matches the world's hold gesture; the full gate remains the hitbox.
  const badge = scene.add.circle(0, 0, 20, 0xfff6df).setStrokeStyle(2, 0x9d6cc7);
  const hand = scene.add.image(0, 1, 'ui_interact_tap').setDisplaySize(34, 34);
  const clock = scene.add.graphics().setVisible(false);
  clock.lineStyle(2.5, 0x805385).strokeCircle(0, 0, 10)
    .beginPath().moveTo(0, -6).lineTo(0, 0).lineTo(5, 3).strokePath();
  const marker = scene.add.container(x + 94, y - 83, [badge, hand, clock])
    .setDepth(12).setName('halloween-boss-portal-marker');
  loop({ targets: marker, y: marker.y - 4, duration: 1100, yoyo: true, ease: 'Sine.easeInOut' });

  let ready: boolean | null = null;
  return {
    setReady(next: boolean): void {
      if (ready === next) return;
      ready = next;
      for (const tween of loops) next ? tween.resume() : tween.pause();
      halo.setAlpha(next ? 0.7 : 0.25);
      energy.setAlpha(next ? 1 : 0.18);
      sparks.setVisible(next);
      hand.setVisible(next);
      clock.setVisible(!next);
      gate.setAlpha(next ? 1 : 0.75);
      badge.setStrokeStyle(2, next ? 0x9d6cc7 : 0xb7a5bb);
    },
    destroy(): void {
      // Scene shutdown owns the objects; explicitly release every looping tween.
      for (const tween of loops) tween.remove();
    },
  };
}

/** Friendly animated landmark; intentional hold with mouse and touch. */
export function installHalloweenBossPortal(scene: Phaser.Scene, interactions: WorldInteractionController): void {
  const { x, y } = halloween.boss.portal;
  let dead = false;
  let entering = false;
  let view: HalloweenBossView | null = null;
  let clockOffset = 0;
  const visuals = createPortalVisuals(scene, x, y);
  const isReady = (): boolean => isEventActive(halloween.event.id)
    && !(view?.status === 'cooldown' && view.nextAvailableAt > Date.now() + clockOffset);
  const updateAvailability = (): void => {
    visuals.setReady(isReady());
    if (view?.status === 'cooldown' && view.nextAvailableAt <= Date.now() + clockOffset) {
      view = null;
      void refresh();
    }
  };
  const refresh = async (): Promise<void> => {
    try {
      const next = await halloweenBossApi.state();
      if (dead) return;
      view = next; clockOffset = next.serverNow - Date.now();
      updateAvailability();
    } catch { /* The arena checks availability again; never invent a local cooldown. */ }
  };
  const zone = scene.add.zone(x, y, 250, 240).setDepth(15).setInteractive({ useHandCursor: true });
  interactions.bind(zone, () => {
    if (entering || !isReady()) return;
    entering = true;
    startTeleport(scene, { target: 'HalloweenBossScene', accent: 0xffc478 });
  }, { holdMs: halloween.boss.holdMs, onTap: () => {
    if (canUseWorldInteraction(useGameStore.getState())) void refresh();
  }, completionFeedback: () => isReady() ? 'success' : 'neutral' });
  updateAvailability();
  void refresh();
  const timer = scene.time.addEvent({ delay: 1000, loop: true, callback: updateAvailability });
  const poll = scene.time.addEvent({ delay: 30_000, loop: true, callback: () => { void refresh(); } });
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    dead = true; timer.remove(); poll.remove(); visuals.destroy();
  });
}
